/**
 * IQAAssessments — Instant Quote Assessment cards
 *
 * When a user clicks any IQA card, a modal captures their name and email
 * (stored to localStorage for future visits). The IQA Pathfinder URL is then
 * opened in a new tab with the user's info pre-filled as URL params.
 *
 * This replaces the hardcoded Kiel Byrne credentials that were previously
 * embedded in all 20 URLs.
 */
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import React, { useState } from "react";

// Base Pathfinder URL — user name/email appended dynamically
const BASE_URL = "https://10k.gopathfinder.net/#/iqa-form";

const IQA_LIST = [
  { name: "Backup & Disaster Recovery", subtitle: "Protect your business data with enterprise-grade backup solutions and comprehensive disaster recovery planning for DC/Maryland/Virginia businesses.", cta: "Assess My Backup Needs", slug: "backupdisasterrecovery" },
  { name: "Colocation", subtitle: "Secure, scalable data center colocation services with 24/7 monitoring and premium connectivity options in the DMV region.", cta: "Explore Colocation Options", slug: "colocation" },
  { name: "Contact Center as a Service", subtitle: "Transform customer service with cloud-based contact center solutions featuring advanced analytics and omnichannel support.", cta: "Upgrade My Contact Center", slug: "contactcenterasaservice" },
  { name: "CX AI", subtitle: "Enhance customer experience with AI-powered automation, intelligent routing, and predictive analytics for superior service delivery.", cta: "Discover AI Solutions", slug: "cxai" },
  { name: "Desktop as a Service", subtitle: "Enable secure remote work with cloud-hosted virtual desktops, ensuring productivity and data protection for distributed teams.", cta: "Get Virtual Desktop Quote", slug: "desktopasaservice" },
  { name: "Email & Productivity", subtitle: "Streamline business communications with enterprise email solutions, collaboration tools, and productivity suites tailored for your organization.", cta: "Optimize Email Systems", slug: "emailproductivity" },
  { name: "Help Desk", subtitle: "Provide exceptional IT support with managed help desk services, ensuring rapid issue resolution and improved user satisfaction.", cta: "Improve IT Support", slug: "helpdesk" },
  { name: "Hosted Fax", subtitle: "Modernize fax communications with secure cloud-based fax services, eliminating hardware while maintaining HIPAA compliance.", cta: "Switch to Cloud Fax", slug: "hostedfax" },
  { name: "Infrastructure as a Service", subtitle: "Scale your IT infrastructure with flexible cloud computing resources, reducing capital expenses while improving performance.", cta: "Build Cloud Infrastructure", slug: "infrastructureasaservice" },
  { name: "Managed Public Cloud", subtitle: "Optimize AWS, Azure, or Google Cloud deployments with expert management, cost optimization, and 24/7 monitoring services.", cta: "Optimize Cloud Costs", slug: "managedpubliccloud" },
  { name: "Managed Services", subtitle: "Focus on core business while we handle your IT operations with comprehensive managed services and proactive monitoring.", cta: "Outsource IT Management", slug: "managedservices" },
  { name: "Mobility", subtitle: "Empower your workforce with secure mobile device management, enterprise mobility solutions, and BYOD policies.", cta: "Mobilize My Business", slug: "mobility" },
  { name: "Penetration Testing", subtitle: "Identify security vulnerabilities with professional penetration testing services, ensuring robust cybersecurity defenses.", cta: "Test Security Defenses", slug: "penetrationtesting" },
  { name: "Physical Security", subtitle: "Protect facilities with integrated physical security systems including access control, surveillance, and monitoring solutions.", cta: "Secure My Facility", slug: "physicalsecurity" },
  { name: "SD-WAN & SASE", subtitle: "Optimize network performance with software-defined WAN and secure access service edge solutions for distributed enterprises.", cta: "Modernize Network", slug: "sdwansase" },
  { name: "Security", subtitle: "Strengthen cybersecurity posture with comprehensive security solutions including firewalls, endpoint protection, and threat monitoring.", cta: "Enhance Cybersecurity", slug: "security" },
  { name: "Telecom Management", subtitle: "Reduce telecom costs and complexity with expert management of voice, data, and wireless services across multiple locations.", cta: "Optimize Telecom Spend", slug: "telecommanagement" },
  { name: "UCaaS", subtitle: "Unify business communications with cloud-based unified communications solutions featuring voice, video, and collaboration tools.", cta: "Upgrade Communications", slug: "ucaas" },
  { name: "VMware", subtitle: "Maximize virtualization ROI with VMware solutions, migration services, and ongoing support for hybrid cloud environments.", cta: "Optimize VMware", slug: "vmware" },
  { name: "Wireless & IoT", subtitle: "Deploy enterprise wireless networks and IoT solutions with secure connectivity, device management, and analytics capabilities.", cta: "Connect Everything", slug: "wirelessiot" },
];

function buildIQAUrl(slug: string, name: string, email: string): string {
  const params = new URLSearchParams({ name, email });
  return `${BASE_URL}/${slug}?${params.toString()}`;
}

function getStoredContact(): { name: string; email: string } {
  try {
    return {
      name: localStorage.getItem("iqa_name") || "",
      email: localStorage.getItem("iqa_email") || localStorage.getItem("prefill_email") || "",
    };
  } catch {
    return { name: "", email: "" };
  }
}

function saveContact(name: string, email: string) {
  try {
    localStorage.setItem("iqa_name", name);
    localStorage.setItem("iqa_email", email);
    localStorage.setItem("prefill_email", email);
  } catch { /* no-op */ }
}

const IQAAssessments = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [pendingSlug, setPendingSlug] = useState("");
  const [name, setName] = useState(() => getStoredContact().name);
  const [email, setEmail] = useState(() => getStoredContact().email);
  const [nameError, setNameError] = useState("");
  const [emailError, setEmailError] = useState("");

  const handleCardClick = (slug: string) => {
    const stored = getStoredContact();
    // If we already have stored contact info, launch directly
    if (stored.name && stored.email) {
      window.open(buildIQAUrl(slug, stored.name, stored.email), "_blank", "noopener,noreferrer");
      return;
    }
    // Otherwise show the info capture modal
    setPendingSlug(slug);
    onOpen();
  };

  const handleSubmit = () => {
    let valid = true;
    if (!name.trim() || name.trim().length < 2) {
      setNameError("Please enter your name");
      valid = false;
    } else {
      setNameError("");
    }
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError("Please enter a valid email");
      valid = false;
    } else {
      setEmailError("");
    }
    if (!valid) return;

    saveContact(name.trim(), email.trim());
    onClose();
    window.open(buildIQAUrl(pendingSlug, name.trim(), email.trim()), "_blank", "noopener,noreferrer");
  };

  return (
    <Box as="section" py={{ base: 8, md: 16 }} px={{ base: 2, md: 8 }}>
      <VStack spacing={4} mb={8}>
        <Heading as="h2" size="lg" textAlign="center">
          Instant Quote Assessments (IQA)
        </Heading>
        <Text fontSize={{ base: "md", md: "lg" }} color="gray.600" textAlign="center">
          Get tailored recommendations from our sales engineers — fill out a quick IQA and receive a response within 24 hours.
        </Text>
      </VStack>

      <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={6}>
        {IQA_LIST.map((iqa) => (
          <Box
            key={iqa.name}
            p={6}
            borderWidth={1}
            borderRadius="xl"
            boxShadow="md"
            display="flex"
            flexDirection="column"
            h="full"
            _hover={{ boxShadow: "lg", transform: "translateY(-2px)" }}
            transition="all 0.2s"
          >
            <Heading as="h3" size="md" mb={3}>
              {iqa.name}
            </Heading>
            <Text fontSize="sm" color="gray.600" mb={4} flex="1" lineHeight="1.5">
              {iqa.subtitle}
            </Text>
            <Button
              onClick={() => handleCardClick(iqa.slug)}
              variant="solid"
              w="full"
              colorScheme="blue"
              size="sm"
            >
              {iqa.cta}
            </Button>
          </Box>
        ))}
      </SimpleGrid>

      {/* Contact capture modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="sm">
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent borderRadius="2xl" mx={4}>
          <ModalHeader fontSize="lg" fontWeight="bold" pb={2}>
            Quick intro before we open your assessment
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Text fontSize="sm" color="gray.600" mb={5}>
              Your name and email will be pre-filled in the assessment form so you don't have to type them again.
            </Text>
            <VStack spacing={4}>
              <FormControl isInvalid={!!nameError}>
                <FormLabel fontSize="sm" fontWeight="semibold">Your Name</FormLabel>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Smith"
                  onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                />
                {nameError && <Text fontSize="xs" color="red.500" mt={1}>{nameError}</Text>}
              </FormControl>
              <FormControl isInvalid={!!emailError}>
                <FormLabel fontSize="sm" fontWeight="semibold">Work Email</FormLabel>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="jane@company.com"
                  onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                />
                {emailError && <Text fontSize="xs" color="red.500" mt={1}>{emailError}</Text>}
              </FormControl>
              <HStack w="full" spacing={3} pt={2}>
                <Button variant="outline" onClick={onClose} size="sm" flex={1}>Cancel</Button>
                <Button colorScheme="blue" onClick={handleSubmit} size="sm" flex={2}>
                  Open My Assessment →
                </Button>
              </HStack>
              <Text fontSize="2xs" color="gray.400" textAlign="center">
                Saved for future assessments. Never shared or sold.
              </Text>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default IQAAssessments;
